/**
 * Geometry and motion planning for the live vehicle marker.
 *
 * Pure by design: no DOM, no MapLibre, no React. Everything here is unit
 * tested, because this is where the bugs live — the rest of the feature is
 * wiring.
 *
 * Coordinates are [longitude, latitude] throughout, matching GeoJSON and
 * MapLibre. The polyline codec emits [lat, lng]; that flip happens at the
 * decode seam in map-style.ts, never here.
 */

export type LngLat = [number, number];

const EARTH_RADIUS_M = 6371008.8;
const M_PER_DEG_LAT = 111320;

const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;

export function distanceM(a: LngLat, b: LngLat): number {
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function bearingDeg(a: LngLat, b: LngLat): number {
  const p1 = toRad(a[1]);
  const p2 = toRad(b[1]);
  const dl = toRad(b[0] - a[0]);
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/** Interpolate along the SHORT arc, so 350deg -> 10deg passes through 0. */
export function lerpHeading(from: number, to: number, t: number): number {
  const diff = ((to - from + 540) % 360) - 180;
  return (from + diff * t + 360) % 360;
}

export function pathLengthM(path: LngLat[]): number {
  let total = 0;
  for (let i = 0; i < path.length - 1; i += 1) total += distanceM(path[i]!, path[i + 1]!);
  return total;
}

// Local planar frame anchored at `origin`. Over the tens of metres between two
// GPS fixes the error is negligible, and it makes perpendicular projection a
// simple dot product instead of spherical trigonometry.
function toLocal(origin: LngLat, p: LngLat): [number, number] {
  const mPerDegLng = M_PER_DEG_LAT * Math.cos(toRad(origin[1]));
  return [(p[0] - origin[0]) * mPerDegLng, (p[1] - origin[1]) * M_PER_DEG_LAT];
}

const lerpPoint = (a: LngLat, b: LngLat, t: number): LngLat => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
];

export function projectOntoPath(
  point: LngLat,
  path: LngLat[],
): { point: LngLat; distanceM: number; alongM: number } {
  if (path.length === 0) return { point, distanceM: 0, alongM: 0 };
  if (path.length === 1) {
    return { point: path[0]!, distanceM: distanceM(point, path[0]!), alongM: 0 };
  }

  let best = { point: path[0]!, distanceM: Infinity, alongM: 0 };
  let travelled = 0;

  for (let i = 0; i < path.length - 1; i += 1) {
    const a = path[i]!;
    const b = path[i + 1]!;
    const segLen = distanceM(a, b);
    const [bx, by] = toLocal(a, b);
    const [px, py] = toLocal(a, point);
    const denom = bx * bx + by * by;
    // Clamped to [0,1] so a point beyond an end projects onto that end rather
    // than extrapolating off the path.
    const t = denom === 0 ? 0 : Math.max(0, Math.min(1, (px * bx + py * by) / denom));
    // `t` is computed in the local METRE frame but consumed in lng/lat. That
    // is valid only because toLocal is a pure per-axis SCALE (lng by
    // mPerDegLng, lat by M_PER_DEG_LAT) with no rotation or shear: scaling
    // each axis independently maps the segment onto itself parameterised the
    // same way, so the fraction along it is identical in both frames. Adding
    // a rotation to toLocal would break this silently — `t` would still look
    // reasonable but land off the segment.
    const proj = lerpPoint(a, b, t);
    const d = distanceM(point, proj);
    if (d < best.distanceM) {
      best = { point: proj, distanceM: d, alongM: travelled + segLen * t };
    }
    travelled += segLen;
  }

  return best;
}

export function pointAtDistance(
  path: LngLat[],
  metres: number,
): { point: LngLat; bearing: number } {
  if (path.length === 0) return { point: [0, 0], bearing: 0 };
  if (path.length === 1) return { point: path[0]!, bearing: 0 };
  if (metres <= 0) return { point: path[0]!, bearing: bearingDeg(path[0]!, path[1]!) };

  let travelled = 0;
  for (let i = 0; i < path.length - 1; i += 1) {
    const a = path[i]!;
    const b = path[i + 1]!;
    const segLen = distanceM(a, b);
    if (travelled + segLen >= metres) {
      const t = segLen === 0 ? 0 : (metres - travelled) / segLen;
      return { point: lerpPoint(a, b, t), bearing: bearingDeg(a, b) };
    }
    travelled += segLen;
  }

  const last = path[path.length - 1]!;
  const prev = path[path.length - 2]!;
  return { point: last, bearing: bearingDeg(prev, last) };
}

export function sliceByDistance(path: LngLat[], fromM: number, toM: number): LngLat[] {
  const reversed = fromM > toM;
  const lo = reversed ? toM : fromM;
  const hi = reversed ? fromM : toM;

  const out: LngLat[] = [pointAtDistance(path, lo).point];
  let travelled = 0;
  for (let i = 0; i < path.length - 1; i += 1) {
    travelled += distanceM(path[i]!, path[i + 1]!);
    if (travelled > lo && travelled < hi) out.push(path[i + 1]!);
  }
  out.push(pointAtDistance(path, hi).point);

  return reversed ? out.reverse() : out;
}

export type MotionMode = 'route' | 'straight' | 'snap';

export const MIN_DURATION_MS = 800;
export const MAX_DURATION_MS = 6000;
export const OFF_ROUTE_THRESHOLD_M = 80;
export const MAX_PLAUSIBLE_KMH = 150;
export const MOVING_THRESHOLD_M = 10;
/** How long the feed may go quiet before the UI stops calling it live. */
export const STALE_AFTER_MS = 20000;

/**
 * Age of the freshest evidence that the bus is still transmitting, or
 * `undefined` while the feed is healthy. `undefined` — never `0` — is the
 * "fresh" signal, so callers can pass it straight through as "not stale".
 *
 * Two independent sources, because neither survives on its own:
 *
 *  - `serverTimestamp` is not refreshed by every socket merge branch. A bus
 *    past its last stop emits `stopsList: []`, and that branch copies only
 *    `vehicleId` and `driverInfo` — so a bus still visibly gliding across the
 *    map would be reported stale.
 *  - a payload that omits `serverTimestamp` entirely would otherwise look
 *    fresh forever, re-opening the never-expiring LIVE pill.
 *
 * So the feed is stale only when BOTH the server clock is old AND nothing has
 * arrived recently: take the smaller of the two ages.
 *
 * `lastChangeAtMs` is when the bus list last changed identity; `-Infinity`
 * means "nothing has ever arrived", which leaves the server clock deciding.
 */
export function resolveStaleAgeMs(
  serverTimestampSec: number | undefined,
  lastChangeAtMs: number,
  nowMs: number,
): number | undefined {
  // serverTimestamp is UNIX SECONDS; everything else here is milliseconds.
  const serverAgeMs = serverTimestampSec ? nowMs - serverTimestampSec * 1000 : Infinity;
  const arrivalAgeMs = nowMs - lastChangeAtMs;
  const ageMs = Math.min(serverAgeMs, arrivalAgeMs);
  // No timestamp and nothing ever received: there is no bus to call stale.
  if (!Number.isFinite(ageMs)) return undefined;
  return ageMs >= STALE_AFTER_MS ? ageMs : undefined;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/**
 * Decide how the marker should travel from where it currently is to the new
 * fix. `fromPoint` is the marker's CURRENT RENDERED position, not the previous
 * fix — a fix arriving mid-animation must continue from where the bus visibly
 * is, or it snaps backwards before moving forward.
 */
export function planMotion(input: {
  fromPoint: LngLat;
  toPoint: LngLat;
  route: LngLat[];
  isDetour: boolean;
  elapsedSinceLastFixMs: number;
}): { path: LngLat[]; durationMs: number; mode: MotionMode } {
  const {
    fromPoint, toPoint, route, isDetour, elapsedSinceLastFixMs,
  } = input;

  const snap = { path: [toPoint, toPoint] as LngLat[], durationMs: 0, mode: 'snap' as const };
  const straight = (durationMs: number) => ({
    path: [fromPoint, toPoint] as LngLat[],
    durationMs,
    mode: 'straight' as const,
  });

  // No previous fix — nothing to animate from.
  if (elapsedSinceLastFixMs <= 0) return snap;

  const directM = distanceM(fromPoint, toPoint);
  const kmh = (directM / 1000) / (elapsedSinceLastFixMs / 3600000);
  // A GPS glitch should not animate a bus across the island.
  if (kmh > MAX_PLAUSIBLE_KMH) return snap;

  const durationMs = clamp(elapsedSinceLastFixMs, MIN_DURATION_MS, MAX_DURATION_MS);

  if (isDetour || route.length < 2) return straight(durationMs);

  const to = projectOntoPath(toPoint, route);
  if (to.distanceM > OFF_ROUTE_THRESHOLD_M) return straight(durationMs);

  const from = projectOntoPath(fromPoint, route);
  if (from.distanceM > OFF_ROUTE_THRESHOLD_M) return straight(durationMs);

  const path = sliceByDistance(route, from.alongM, to.alongM);
  if (pathLengthM(path) === 0) return straight(durationMs);

  return { path, durationMs, mode: 'route' };
}
